import { useIsSmOrSmaller } from '@dreamstack/feature-components/src/hooks/useWindowSize'
import { HeadingH3 } from '@dreamstack/feature-components/src/layout/Texts'
import { useTranslation } from '@dreamstack/i18n'
import range from 'lodash/range'
import dynamic from 'next/dynamic'
import type { FunctionComponent } from 'react'
import { Fragment } from 'react'
import { formatYears } from './HomeOwnershipReport/Settings'
import { useGraphFontsizeCb } from './HomeOwnershipReport/useGraphFontsize'
import {
  MIN_MAX_MARKER_COLOR,
  MIN_MAX_RECT_FILL_COLOR,
  MIN_MAX_RECT_STROKE_COLOR,
  NODE_COLOR,
} from './RegionGraphs/libs/colors'
import { SquareNode } from './RegionGraphs/libs/customNodes'
import { useGetMinMaxDataValues } from './RegionGraphs/libs/getMinMaxDataValues'
import { getYScaleValue } from './RegionGraphs/libs/getYScaleValue'
import type {
  CandleStickGraphProps,
  DataPoint,
} from './RegionGraphs/libs/typings'
import { useGetFormattedToLocal } from './RegionGraphs/libs/useGetFormattedToLocal'

const ResponsiveScatterPlot = dynamic(
  () => import('@nivo/scatterplot').then((mod) => mod.ResponsiveScatterPlot),
  {
    ssr: false,
  }
)

// variables
const MIN_MAX_RECT_WIDTH = 1
const MIN_MAX_MARKER_WIDTH = 30
const MIN_MAX_MARKER_WIDTH_MOBILE = 20
const LABEL_X_OFFSET = 15
const LABEL_Y_OFFSET = 5

//TODO: Remove this if the area is not needed anymore -> Could still be a cool feature to add?
// const AreaLayer = ({ nodes, xScale, yScale }) => {
//   const areaGenerator = area()
//     .x((d) => xScale(d.data.x))
//     .y0((d) => yScale(d.data.low))
//     .y1((d) => yScale(d.data.high))
//     .curve(curveMonotoneX)

//   return <path d={areaGenerator(nodes)} fill="rgba(69, 98, 123, 0.3)" />
// }

export const CandleStickGraph: FunctionComponent<
  React.PropsWithChildren<CandleStickGraphProps>
> = ({ data, title, rangeStep }) => {
  const graphFontSize = useGraphFontsizeCb()
  const formatToLocalString = useGetFormattedToLocal()
  // check if Windows is sm or smaller, so that we can hide the labels next to each point if window too small
  const isMobile = useIsSmOrSmaller()
  const t = useTranslation()

  const { maxXDataValue, minXDataValue, maxYDataValue, minYDataValue } =
    useGetMinMaxDataValues(data)

  const yScaleValues = range(
    getYScaleValue(minYDataValue, rangeStep),
    getYScaleValue(maxYDataValue, rangeStep, true) + 1,
    rangeStep
  )
  const xScaleValues = range(minXDataValue, maxXDataValue + 1, 2)

  const minMaxMarkerWidth = isMobile
    ? MIN_MAX_MARKER_WIDTH_MOBILE
    : MIN_MAX_MARKER_WIDTH

  const CandlestickLayer = ({
    xScale,
    yScale,
  }: {
    xScale: any
    yScale: any
  }) => {
    return (
      <Fragment>
        {data[0].data.map((point: any, i: number) => {
          const x0 = xScale(point.x)
          const y0 = yScale(point.low)
          const y1 = yScale(point.high)

          const width = MIN_MAX_RECT_WIDTH
          const height = y0 - y1

          return (
            <g key={i}>
              <rect
                x={x0 - MIN_MAX_RECT_WIDTH / 2}
                y={y1}
                width={width}
                height={height}
                fill={MIN_MAX_RECT_FILL_COLOR}
                stroke={MIN_MAX_RECT_STROKE_COLOR}
                strokeWidth={2}
              />
              <line
                x1={x0 - minMaxMarkerWidth / 2}
                y1={y0}
                x2={x0 + minMaxMarkerWidth / 2}
                y2={y0}
                stroke={MIN_MAX_MARKER_COLOR}
                strokeWidth={4}
              />
              <line
                x1={x0 - minMaxMarkerWidth / 2}
                y1={y1}
                x2={x0 + minMaxMarkerWidth / 2}
                y2={y1}
                stroke={MIN_MAX_MARKER_COLOR}
                strokeWidth={4}
              />
            </g>
          )
        })}
      </Fragment>
    )
  }

  // creates the label layer to display point value next to the point
  const Labels = ({ xScale, yScale }: { xScale: any; yScale: any }) => {
    if (isMobile) return null
    return (
      <div>
        {data[0].data.map((point: any, i: number) => {
          const x = xScale(point.x)
          const y = yScale(point.y)

          return (
            <text
              key={i}
              x={x + LABEL_X_OFFSET}
              y={y + LABEL_Y_OFFSET}
              fontSize={graphFontSize()}
              fill={NODE_COLOR}
            >
              {formatToLocalString(point.y)}
            </text>
          )
        })}
      </div>
    )
  }

  return (
    <div className="col-span-full bg-accentroAqua-100 h-auto lg:col-start-3 lg:col-span-8">
      <div className="ml-px-40 pt-px-32">
        <HeadingH3 className="mb-px-48">{title}</HeadingH3>
      </div>
      <div className="h-px-576 overflow-hidden">
        <ResponsiveScatterPlot
          data={data}
          margin={{
            top: 30,
            right: isMobile ? 40 : 140,
            bottom: 70,
            left: isMobile ? 70 : 90,
          }}
          xScale={{ type: 'linear', min: minXDataValue - 1 }}
          yScale={{
            type: 'linear',
            min: getYScaleValue(minYDataValue, rangeStep),
            max: getYScaleValue(maxYDataValue, rangeStep, true),
          }}
          theme={{
            text: {
              fontSize: graphFontSize(),
              fontFamily: 'Montserrat',
            },
            axis: {
              legend: {
                text: {
                  fontSize: '20px',
                  fontWeight: 500,
                },
              },
            },
          }}
          gridXValues={xScaleValues}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: t(
              'accentro:districtLandingPageGraphs.regionAverageGraph.year'
            ),
            legendPosition: 'middle',
            legendOffset: 46,
            tickValues: xScaleValues,
            format: (value) => {
              if (isMobile) return formatYears(value)
            },
          }}
          gridYValues={yScaleValues}
          axisLeft={{
            tickValues: yScaleValues,
            format: (val) => formatToLocalString(val),
          }}
          colors={[NODE_COLOR]}
          nodeComponent={SquareNode}
          nodeSize={isMobile ? 7.5 : 15}
          layers={[
            'grid',
            'axes',
            CandlestickLayer,
            'nodes',
            'markers',
            'mesh',
            'legends',
            Labels,
          ]}
          tooltip={({ node }) => {
            const dataPoint = node.data as DataPoint
            return (
              <div
                style={{
                  color: node.color,
                  background: '#fff',
                  padding: '12px 16px',
                }}
              >
                <strong>{node.serieId}</strong>
                <br />
                <strong>{`${t(
                  'accentro:districtLandingPageGraphs.regionAverageGraph.year'
                )}${' '}${node.formattedX}`}</strong>
                <br />
                <strong>
                  {`${t(
                    'accentro:districtLandingPageGraphs.scatterPlotWithRegressionLine.average'
                  )} ${node.serieId}: `}{' '}
                </strong>
                {`${formatToLocalString(dataPoint.y)} EUR`}
                <br />
                <strong>
                  {`${t(
                    'accentro:districtLandingPageGraphs.scatterPlotWithRegressionLine.lowest'
                  )} ${node.serieId}: `}{' '}
                </strong>
                {`${formatToLocalString(dataPoint.low)} EUR`}
                <br />
                <strong>{`${t(
                  'accentro:districtLandingPageGraphs.scatterPlotWithRegressionLine.highest'
                )} ${node.serieId}: `}</strong>
                {`${formatToLocalString(dataPoint.high)} EUR`}
              </div>
            )
          }}
        />
      </div>
    </div>
  )
}
